import React, { useEffect, useState, useContext } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import ConfirmDialog from "../components/ConfirmDialog";
import { Ticket } from "lucide-react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../../Context/GlobalContext";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

export default function CouponSection({ userID }) {
  const { getMerchantCouponCodes, createMerchantCouponCode, disableMerchantCouponCode, } = useOrderSettings();

  const { api } = useContext(GlobalContext);
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState({ open: false, onConfirm: null, title: "", message: "" });

  const [form, setForm] = useState({
    couponCode: "", discount: "", discountType: "", new_user: "", minOrderAmount: "",
    maxDiscountAmount: "", couponTitle: "", couponDiscription: "",singleUse: "",
  });

  const load = async () => {
    if (!userID) return;

    try {
      const result =
        await getMerchantCouponCodes(userID);

      if (result.success) {
        setList(result.data);
      } else {
        setList([]);
        // optional:
        // toast.error(result.message);
      }
    } catch {
      setList([]);
    }
  };

  const save = async () => {
    const f = form;

    if (
      !f.couponCode ||
      !f.discount ||
      !f.discountType ||
      !f.new_user ||
        !f.singleUse ||
      !f.minOrderAmount ||
      !f.maxDiscountAmount ||
      !f.couponTitle ||
      !f.couponDiscription
    ) {
      return toast.error("Fill all coupon fields");
    }

    try {
      const result =
        await createMerchantCouponCode({
          userid: userID,
          couponCode: f.couponCode,
          discount: Number(f.discount),
          discountType: f.discountType,
          new_user: f.new_user,
          singleUse: f.singleUse,
          minOrderAmount: Number(f.minOrderAmount),
          maxDiscountAmount: Number(f.maxDiscountAmount),
          couponTitle: f.couponTitle,
          couponDiscription: f.couponDiscription,
        });

      if (result.success) {
        toast.success(result.message);

        setForm({
          couponCode: "",
          discount: "",
          discountType: "",
          new_user: "",
          singleUse: "",
          minOrderAmount: "",
          maxDiscountAmount: "",
          couponTitle: "",
          couponDiscription: "",
        });

        load();
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Failed");
    }
  };

  const ask = (title, message, cb) => setConfirm({ open: true, title, message, onConfirm: () => { setConfirm({ open: false }); cb(); } });
  const cancel = () => setConfirm({ open: false, onConfirm: null, title: "", message: "" });

  const removeRow = (couponId) => {
    ask(
      "Delete coupon?",
      "This action cannot be undone.",
      async () => {
        try {
          const result =
            await disableMerchantCouponCode({
              userid: userID,
              couponId,
              operation: "DELETE",
            });

          if (result.success) {
            toast.success(result.message);
            load();
          } else {
            toast.error(result.message);
          }
        } catch {
          toast.error("Failed");
        }
      }
    );
  };

  useEffect(() => { load(); }, [userID]); // eslint-disable-line

  return (
    <>
      {!!list.length && (
        <SectionCard icon={Ticket} title="Existing Coupons">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {list.map((c) => (
              <div key={c.id} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">Code: {c.coupon_code}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => removeRow(c.id)}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-gray-600">
                  <div>
                    <div className="font-medium text-gray-900">
                      {c.discount_type === "fixed" ? "₹" : ""}
                      {Number(c.discount).toFixed(2)}
                      {c.discount_type === "fixed" ? "" : "%"}
                    </div>
                    <div>Discount</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">₹{Number(c.min_amount).toFixed(2)}</div>
                    <div>Min Amount</div>
                  </div>
                  <div className="col-span-2">
                    <div className="font-medium text-gray-900">{c.coupon_title}</div>
                    <div>Title</div>
                  </div>
                  <div className="col-span-2">
                    <div className="font-medium text-gray-900">{c.coupon_description}</div>
                    <div>Description</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard icon={Ticket} title="Create New Coupon">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div><Label>Coupon Code</Label><Input value={form.couponCode} onChange={(e) => setForm(s => ({ ...s, couponCode: e.target.value }))} /></div>
          <div><Label>Discount</Label><Input value={form.discount} onChange={(e) => setForm(s => ({ ...s, discount: e.target.value }))} /></div>
          <div>
            <Label>Discount Type</Label>
            <Select value={form.discountType} onChange={(e) => setForm(s => ({ ...s, discountType: e.target.value }))}>
              <option value="">Select</option>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </Select>
          </div>
          <div>
            <Label>New Customer</Label>
            <Select value={form.new_user} onChange={(e) => setForm(s => ({ ...s, new_user: e.target.value }))}>
              <option value="">Select</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </Select>
          </div>
           <div>
            <Label>Single Use</Label>
            <Select value={form.singleUse} onChange={(e) => setForm(s => ({ ...s, singleUse: e.target.value }))}>
              <option value="">Select</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </Select>
          </div>
          <div><Label>Min Order Amount</Label><Input value={form.minOrderAmount} onChange={(e) => setForm(s => ({ ...s, minOrderAmount: e.target.value }))} /></div>
          <div><Label>Max Discount Amount</Label><Input value={form.maxDiscountAmount} onChange={(e) => setForm(s => ({ ...s, maxDiscountAmount: e.target.value }))} /></div>
          <div><Label>Title</Label><Input value={form.couponTitle} onChange={(e) => setForm(s => ({ ...s, couponTitle: e.target.value }))} /></div>
          <div className="lg:col-span-3"><Label>Description</Label><Input value={form.couponDiscription} onChange={(e) => setForm(s => ({ ...s, couponDiscription: e.target.value }))} /></div>
        </div>
        <div className="mt-4">
          <button onClick={save} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            Create Coupon
          </button>
        </div>
      </SectionCard>
    </>
  );
}
